/**
 * Offre d'assistance — types partagés.
 *
 * La formule est choisie au PARAMÉTRAGE de la garantie, pas à la souscription :
 * tous les contrats issus d'une garantie portent la même formule, figée sur le
 * contrat au moment de la vente.
 */

export interface AssistanceBenefitBlock {
  label: string;
  value: string;
}

export interface AssistanceBenefits {
  blocks: AssistanceBenefitBlock[];
  /** Ligne véhicule de prêt, propre aux formules Premium. `null` pour Standard. */
  loaner: string | null;
}

export interface AssistanceFormula {
  id: number;
  code: string;
  label: string;
  tier: 'standard' | 'premium';
  loanerDays: number;
  benefits: AssistanceBenefits | null;
  isActive: boolean;
  displayOrder: number;
}

/**
 * Habillage éditorial de l'encart imprimé sur les conditions particulières.
 * Ne porte que le texte : la formule et les prestations viennent du contrat.
 */
export interface AssistanceArticle {
  id: string;
  articleKey: string;
  title: string;
  /** `{formule}` y est substitué par la formule souscrite. */
  intro: string;
  /** `{numero}` y est substitué par `phone`. */
  footer: string | null;
  phone: string | null;
  /** Adresse substituée à `{email}`. */
  email: string | null;
  isVisible: boolean;
}

export type AssistancePriceSource = 'own' | 'billed-parent' | 'catalog';

export interface AssistanceResolvedPrice {
  formulaId: number;
  price: number;
  source: AssistancePriceSource;
}

/** Une case de la matrice tarifaire d'une société. */
export interface AssistanceSocietyPrice {
  formulaId: number;
  duration: number;
  price: number;
  source: AssistancePriceSource;
  /** Identifiant de la ligne négociée quand `source === 'own'` — sert à la supprimer. */
  customPricingId: number | null;
}

/**
 * Durées de garantie éligibles. Les durées vendables étant 3, 6, 12, 24, 36, 48 et
 * 60 mois, la grille tarifaire couvre exactement les durées éligibles — aucun
 * prorata n'est nécessaire.
 */
export const ASSISTANCE_ELIGIBLE_DURATIONS = [6, 12, 24, 36, 48, 60] as const;

export function isAssistanceEligibleDuration(duration: number | undefined | null): boolean {
  if (duration === undefined || duration === null) return false;
  return (ASSISTANCE_ELIGIBLE_DURATIONS as readonly number[]).includes(duration);
}

/**
 * Prix d'achat d'une formule : ce qu'Affinicar paie à Mutuaide. CONFIDENTIEL — il
 * révèle la marge ; servi aux seuls internes.
 */
export interface AssistancePurchasePrice {
  id: number;
  formulaId: number;
  duration: number;
  /** HT. */
  price: number;
  /** Premier jour d'application, `AAAA-MM-JJ`, heure de Paris. */
  effectiveDate: string;
  createdAt: string;
  /** Email de l'auteur, `null` pour la grille initiale. */
  createdBy: string | null;
}

/** Une case de la grille d'achat : version en vigueur, programmées, historique. */
export interface PurchasePriceCell {
  formulaId: number;
  duration: number;
  current: AssistancePurchasePrice | null;
  /** La plus proche d'abord. */
  upcoming: AssistancePurchasePrice[];
  /** La plus récente d'abord. */
  history: AssistancePurchasePrice[];
}
